import Phaser from 'phaser';
import { GAME_W, GAME_H, BASE_SEAT_ORIGIN, SEAT_RISE, SCOOP_STACK_DY } from '../config.ts';
import { type DifficultyId } from '../levels.ts';
import { Audio } from '../audio.ts';

interface CardSpec {
  id: DifficultyId;
  /** Spoken label (pre-readers can't read it — it's a voice + picture cue). */
  label: string;
  accent: number;
  /** Flavors stacked on the card's demo ice cream (count = difficulty cue). */
  scoops: string[];
  /** Star rating shown across the header. */
  stars: number;
  /** Coins shown at the foot, hinting "you'll handle money". */
  coins: number[];
}

const CARDS: CardSpec[] = [
  { id: 'easy', label: 'Easy', accent: 0xa9e0c2, scoops: ['strawberry'], stars: 1, coins: [1] },
  { id: 'medium', label: 'Medium', accent: 0xfcd24b, scoops: ['vanilla', 'chocolate'], stars: 2, coins: [1, 2] },
  { id: 'hard', label: 'Hard', accent: 0xf48fb1, scoops: ['mango', 'mint', 'blueberry'], stars: 3, coins: [1, 2, 5] },
];

const CARD_W = 320;
const CARD_H = 460;
const CARD_Y = GAME_H * 0.52;

/**
 * The start screen: a picture-driven difficulty picker. Each card shows a
 * little ice cream (more scoops = harder), a star rating, and coins, plus a
 * spoken label on tap — no reading required. Tapping a card starts the game at
 * that difficulty.
 */
export class MenuScene extends Phaser.Scene {
  constructor() {
    super('Menu');
  }

  create(): void {
    this.add.image(GAME_W / 2, GAME_H / 2, 'bg').setDisplaySize(GAME_W, GAME_H).setDepth(-10);
    // Soft scrim so the busy shop calms down behind the menu cards.
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0xfff6ec, 0.42).setDepth(0);

    this.buildTitle();
    CARDS.forEach((spec, i) => this.buildCard(spec, i));
    this.buildSoundButton();

    // Bear shopkeeper waves from behind the counter.
    const bear = this.add.image(GAME_W * 0.085, GAME_H * 0.96, 'animal_bear').setOrigin(0.5, 1).setScale(0.95).setDepth(5);
    this.tweens.add({ targets: bear, angle: { from: -5, to: 5 }, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.InOut' });

    this.input.once('pointerdown', () => Audio.unlock());
  }

  private buildTitle(): void {
    const cx = GAME_W / 2;
    const y = GAME_H * 0.16;
    const w = 760;
    const h = 150;
    const g = this.add.graphics().setDepth(2);
    g.fillStyle(0x000000, 0.08).fillRoundedRect(cx - w / 2 + 6, y - h / 2 + 10, w, h, 36);
    g.fillStyle(0xfff6db, 1).fillRoundedRect(cx - w / 2, y - h / 2, w, h, 36);
    g.lineStyle(7, 0xef88ab, 1).strokeRoundedRect(cx - w / 2, y - h / 2, w, h, 36);

    this.add
      .text(cx, y - 22, 'Tiny Ice Cream Shop', {
        fontFamily: 'Fredoka, system-ui, sans-serif',
        fontSize: '64px',
        fontStyle: 'bold',
        color: '#5b4636',
      })
      .setOrigin(0.5)
      .setDepth(3);
    this.add
      .text(cx, y + 38, 'Tap a cone to play', {
        fontFamily: 'Fredoka, system-ui, sans-serif',
        fontSize: '30px',
        color: '#9a8775',
      })
      .setOrigin(0.5)
      .setDepth(3);
  }

  private buildCard(spec: CardSpec, index: number): void {
    const gap = 60;
    const totalW = CARDS.length * CARD_W + (CARDS.length - 1) * gap;
    const startX = (GAME_W - totalW) / 2 + CARD_W / 2;
    const x = startX + index * (CARD_W + gap);
    const y = CARD_Y;
    const card = this.add.container(x, y).setDepth(4);

    const left = -CARD_W / 2;
    const top = -CARD_H / 2;
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.1).fillRoundedRect(left + 7, top + 12, CARD_W, CARD_H, 34);
    g.fillStyle(0xffffff, 1).fillRoundedRect(left, top, CARD_W, CARD_H, 34);
    // Accent header band (rounded only at the top via a clipped fill).
    g.fillStyle(spec.accent, 1).fillRoundedRect(left, top, CARD_W, 130, 34);
    g.fillStyle(spec.accent, 1).fillRect(left, top + 90, CARD_W, 40);
    g.lineStyle(6, 0xe2d3c0, 1).strokeRoundedRect(left, top, CARD_W, CARD_H, 34);
    card.add(g);

    // Stars across the header.
    const starGap = 64;
    const sx = -((spec.stars - 1) * starGap) / 2;
    for (let i = 0; i < spec.stars; i++) {
      card.add(this.add.image(sx + i * starGap, top + 62, 'star').setDisplaySize(58, 58));
    }

    // Demo ice cream (cone + scoops) — more scoops reads as harder.
    this.addMiniIceCream(card, 0, 70, spec.scoops);

    // Coins at the foot hint that money is involved.
    const coinGap = 64;
    const cxStart = -((spec.coins.length - 1) * coinGap) / 2;
    spec.coins.forEach((v, i) => {
      card.add(this.add.image(cxStart + i * coinGap, CARD_H / 2 - 52, `coin_${v}`).setDisplaySize(52, 52));
    });

    // Whole card is one big tap target.
    const hit = this.add
      .rectangle(0, 0, CARD_W, CARD_H, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    card.add(hit);
    hit.on('pointerdown', () => this.choose(spec, card));

    // Gentle idle float to invite a tap.
    this.tweens.add({
      targets: card,
      y: y - 10,
      duration: 1400 + index * 160,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  }

  /** A small static cone with the given scoops, added into `card`. */
  private addMiniIceCream(card: Phaser.GameObjects.Container, cx: number, cy: number, flavors: string[]): void {
    const scale = 0.78;
    const SCOOP_W = 132 * scale;
    const SCOOP_H = SCOOP_W * (175 / 200);
    const baseH = 200 * scale;
    const inner = this.add.container(cx, cy);

    const base = this.add
      .image(0, 0, 'base_cone')
      .setDisplaySize(160 * scale, baseH)
      .setOrigin(0.5, BASE_SEAT_ORIGIN.cone);
    inner.add(base);

    let topY = 0;
    flavors.forEach((f, i) => {
      const yy = -SEAT_RISE * scale - i * SCOOP_STACK_DY * scale;
      topY = yy;
      inner.add(this.add.image(0, yy, `scoop_${f}`).setDisplaySize(SCOOP_W, SCOOP_H));
    });
    // Center the stack vertically in the card's picture area.
    const stackTop = topY - SCOOP_H / 2;
    const baseBottom = baseH * (1 - BASE_SEAT_ORIGIN.cone);
    inner.y = cy - (stackTop + baseBottom) / 2;
    card.add(inner);
  }

  private buildSoundButton(): void {
    const sound = this.add.image(64, 60, 'sound_icon').setDisplaySize(74, 74).setDepth(30);
    sound.setAlpha(Audio.isMuted ? 0.4 : 1);
    sound.setInteractive({ useHandCursor: true });
    sound.on('pointerdown', () => {
      const muted = Audio.toggleMute();
      sound.setAlpha(muted ? 0.4 : 1);
    });
  }

  private choose(spec: CardSpec, card: Phaser.GameObjects.Container): void {
    Audio.unlock();
    Audio.pop();
    Audio.speak(spec.label, { pitch: 1.25 });
    this.tweens.killTweensOf(card);
    this.tweens.add({
      targets: card,
      scale: { from: 1, to: 1.08 },
      duration: 120,
      yoyo: true,
      onComplete: () => {
        this.cameras.main.fadeOut(280, 205, 234, 246);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('Play', { difficulty: spec.id });
        });
      },
    });
  }
}
